/**
 * ResizeObserver 的一个简单 polyfill，API 与原生相同
 * 使用 setInterval 轮询实现，默认轮询时长为 200ms
 *
 * !注意: 用于比对的是元素本身的 el.getBoundingClientRect() 返回值，某些情况下会和 native 触发时机略有区别
 * ! 此外，传入构造函数的回调函数中，第一个参数 ResizeObserverEntry 的数据会失真，需要自行从 DOM 中获取
 */
var ResizeObserverPolyfilled = /** @class */ (function () {
    function ResizeObserverPolyfilled(callback) {
        var _this = this;
        /** 存储 ResizeObserver.observe 传入的 Element 和 Options KV */
        this.targetOptionMap = new Map();
        /** 轮询定时器返回值 */
        this.pollTimer = null;
        /** 上次轮询，被 observe 元素的 DOMRect 属性，用于 diff 触发回调 */
        this.lastRectMap = new Map();
        /** 轮询时长，如果希望修改，可以在外部 as ResizeObserverPolyfilled 修改掉 */
        this.pollTimeout = DEFAULT_INTERVAL_TIMEOUT;
        /**
         * 定时器轮询回调函数
         * 轮询检查被监听元素的尺寸变更情况，在变更后触发 constructor 中传入的 callback
         */
        this.onPollTimeout = function () {
            var entries = [];
            // 如果某些元素，此次轮询已经从文档中移除，则从 map 中移除掉它
            _this.removeDisconnectedElements();
            // 遍历所有需要监听的元素，获取尺寸对比是否有变更
            _this.targetOptionMap.forEach(function (options, el) {
                var rectCurrent = el.getBoundingClientRect();
                var rectBefore = _this.lastRectMap.get(el);
                // 否则，将当前元素尺寸和上次存储的值进行 diff
                var isChange = _this.diffRect(rectBefore, rectCurrent, options === null || options === void 0 ? void 0 : options.box);
                // 如果有变更，则推入 entries 数组中
                if (isChange) {
                    entries.push(_this.assembleEntries(el, rectCurrent));
                }
                // 把本次获取到的元素尺寸存起来
                _this.lastRectMap.set(el, rectCurrent);
            });
            // 检查 entries 数组，如果有元素尺寸变更，触发回调函数
            if (entries.length) {
                _this.callback(entries, _this);
            }
        };
        this.callback = callback;
    }
    ResizeObserverPolyfilled.prototype.disconnect = function () {
        this.targetOptionMap.clear();
        this.setPoll(false);
    };
    ResizeObserverPolyfilled.prototype.observe = function (target, options) {
        this.targetOptionMap.set(target, options);
        this.setPoll(true);
    };
    ResizeObserverPolyfilled.prototype.unobserve = function (target) {
        this.targetOptionMap.delete(target);
    };
    /**
     * 取消上次轮询，重新开始轮询
     * @param isStart 是否开始轮询，false 为取消
     */
    ResizeObserverPolyfilled.prototype.setPoll = function (isStart) {
        if (isStart === void 0) { isStart = true; }
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        if (isStart) {
            this.pollTimer = setInterval(this.onPollTimeout, this.pollTimeout);
        }
    };
    /**
     * 遍历存储变量，移除所有已经从 DOM 上移除的元素
     * 避免内存无法被正确释放
     */
    ResizeObserverPolyfilled.prototype.removeDisconnectedElements = function () {
        var _this = this;
        this.targetOptionMap.forEach(function (_, el) {
            if (!el.isConnected) {
                _this.targetOptionMap.delete(el);
                _this.lastRectMap.delete(el);
            }
        });
    };
    /**
     * 组装 callback 使用的 entries
     * Todo: 看目前使用情况没有用到 ResizeObserverEntry 的属性,这里计算起来复杂且耗时长，因此这里直接返回为空
     * @param target
     * @param rect
     * @returns
     */
    ResizeObserverPolyfilled.prototype.assembleEntries = function (target, rect) {
        return {
            target: target,
            borderBoxSize: [],
            contentBoxSize: [],
            contentRect: rect,
            devicePixelContentBoxSize: [],
        };
    };
    /**
     * 对比同一元素前后的 RectDOM 尺寸数据，根据 Options 规则判断元素是否变更
     * https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver/observe
     * @param rectBefore 之前的尺寸
     * @param rectAfter 之后的尺寸
     * @param boxOptions 对比规则
     * @returns 是否变更
     */
    ResizeObserverPolyfilled.prototype.diffRect = function (rectBefore, rectAfter, boxOptions) {
        if (boxOptions === void 0) { boxOptions = 'content-box'; }
        if (!rectBefore)
            return true;
        var diffAttrs = DIFF_ATTR_RULE_MAP[boxOptions];
        return diffAttrs.some(function (attr) { return rectBefore[attr] !== rectAfter[attr]; });
    };
    return ResizeObserverPolyfilled;
}());
export { ResizeObserverPolyfilled };
/** 默认轮询时长 */
var DEFAULT_INTERVAL_TIMEOUT = 200;
export var DIFF_ATTR_RULE_MAP = {
    'border-box': ['height', 'width', 'left', 'right', 'top', 'bottom'],
    'content-box': ['height', 'width'],
    'device-pixel-content-box': ['height', 'width'],
};
// 默认导出的是 native 实现，如果 window 未实现，则导出 polyfill
export var ResizeObserver = window.ResizeObserver || ResizeObserverPolyfilled;
